import { ActionsAdAssistant } from "../../../Actions/ActionsAdAssistant/ActionsAdAssistant";
import Actions from "../../../Actions";
import { useTranslation } from "react-i18next";

export const useAdUpdate = (currentAd, allAdCombinations, userProfile) => {
  const { t } = useTranslation();

  const findNextAvailableAd = (combinations) => {
    const nextAd = combinations.find((ad) => ad.status === "temp" || ad.status === "unused");
    if (nextAd) {
      ActionsAdAssistant.setCurrentAd(nextAd);
    }
  };

  const getReorderedCombinations = (combinations) => {
    return [
      ...combinations.filter((ad) => ad.status === "used" || ad.status === "dismissed"),
      ...combinations.filter((ad) => ad.status !== "used" && ad.status !== "dismissed")
    ];
  };

  const updateAdCombinations = (payload, shouldFindNext = false) => {
    const newAllAdCombinations = allAdCombinations.map((ad) => {
      if (ad.id === currentAd.id) {
        return { ...payload };
      }
      return ad;
    });

    const reorderedCombinations = getReorderedCombinations(newAllAdCombinations);
    ActionsAdAssistant.setAllAdCombinations(reorderedCombinations);

    if (shouldFindNext) {
      findNextAvailableAd(reorderedCombinations);
    } else {
      ActionsAdAssistant.setCurrentAd(payload);
    }
  };

  const updateExistingAd = (updates, shouldFindNext) => {
    ActionsAdAssistant.updateAd(
      {
        updatedBy: userProfile.userName,
        ...updates
      },
      currentAd.id
    );

    const updateSuccessfully = ActionsAdAssistant.updateAd.completed.listen((result) => {
      const { payload } = result;

      updateAdCombinations(payload, shouldFindNext);
      updateSuccessfully();
    });

    const updateFailed = ActionsAdAssistant.updateAd.failed.listen(() => {
      ActionsAdAssistant.setCurrentAd(currentAd);
      Actions.addNotification(t("Update failed, please try again."));
      updateFailed();
    });
  };

  const createNewAd = (updates, shouldFindNext) => {
    // Ensure 'status' is set to 'unused' if not provided
    const adUpdates = {
      ...updates,
      status: updates.status || "unused"
    };

    ActionsAdAssistant.createAd({
      ...currentAd,
      createdBy: userProfile.userId,
      ...adUpdates,
      version: 1
    });

    const createSuccessfully = ActionsAdAssistant.createAd.completed.listen((result) => {
      const { payload } = result;

      updateAdCombinations(payload, shouldFindNext);
      createSuccessfully();
    });

    const createFailed = ActionsAdAssistant.createAd.failed.listen(() => {
      ActionsAdAssistant.setCurrentAd(currentAd);
      Actions.addNotification(t("Creation failed, please try again."));
      createFailed();
    });
  };

  const updateAd = (updates = {}) => {
    const isNewAd = currentAd.status === "temp";
    const shouldFindNext = updates.status === "used" || updates.status === "dismissed";

    if (currentAd.id && !isNewAd) {
      updateExistingAd(updates, shouldFindNext);
    } else {
      createNewAd(updates, shouldFindNext);
    }
  };

  const updateMediaAssets = ({ updatedMediaAssets, showNotification = false }) => {
    const newMediaAssets = {
      ...currentAd.mediaAssets,
      ...updatedMediaAssets
    };

    const newCurrentAd = { ...currentAd, mediaAssets: newMediaAssets };
    ActionsAdAssistant.setCurrentAd(newCurrentAd);

    if (showNotification) {
      Actions.addNotification(t("Saved correctly"));
    }

    updateAd({ mediaAssets: updatedMediaAssets });
  };

  return {
    updateAd,
    updateMediaAssets,
    findNextAvailableAd
  };
};
